import { Component, Input, Output, EventEmitter } from '@angular/core';
import * as Highcharts from 'highcharts';
import { catchError, forkJoin, map, Observable, of, Subscription, tap } from 'rxjs';
import { getChartEvents, spiderChartConfig } from '../highcharts-options';
import {
  QuickBuild,
  QuickBuildService,
  UIFilter,
  UIFilterGroup,
  UniqueIdGenerator,
} from 'src/app/people/quick-build.service';
import {
  BaseReportComponent,
  ReportResponseWithName,
  ChartContextMenuItem,
} from '../base-report.component';
import { Log } from 'src/app/logger.service';
import { StreamsSubscribeDrawerService } from '../../streams-subscribe-drawer/streams-subscribe-drawer.service';
import { TenureCohortComparisonConfig } from '../interfaces/report-config.interfaces';
import { AuthService } from 'src/app/auth/service/auth.service';
import { PeopleReportService } from 'src/app/people/report-service.service';
import { ToggleOption } from '../../components/option-toggle/option-toggle.component';
import { ReportTenureDistributionParams, SearchFilter } from 'ldt-people-api';
import * as dayjs from 'dayjs';
import { StatsigService } from '@statsig/angular-bindings';
import { WorkforceLayoutService } from 'src/app/workforce/workforce-layout.service';

const REPORT_NAME = 'tenure_distribution';

// Component-specific chart options for the tenure cohort spider chart
const TenureCohortComparisonChartOptions: Highcharts.Options = {
  title: {
    text: 'Current Employee Tenure Breakdown',
  },
  xAxis: {
    labels: {
      style: {
        fontWeight: 'bold',
      },
    },
  },
  yAxis: {
    labels: {
      formatter: function () {
        const pointValue = this.value as number;
        return pointValue.toFixed(0) + '%';
      },
    },
  },
  legend: {
    useHTML: true,
    layout: 'vertical',
    align: 'right',
    verticalAlign: 'top',
    y: 20,
    width: '25%',
  },
};

@Component({
  selector: 'app-reports-tenure-cohort-comparison',
  templateUrl: './tenure-cohort-comparison.component.html',
  styleUrls: ['./tenure-cohort-comparison.component.scss'],
  standalone: false,
})
export class TenureCohortComparisonComponent extends BaseReportComponent<TenureCohortComparisonConfig> {
  @Input() quickBuilds: QuickBuild[];
  @Input() reportTarget: string;
  @Input() boxShadow: boolean = true;

  protected readonly CHART_ID = 'tenure-cohort-comparison';
  protected readonly CHART_DATA_DESC = 'Current employee tenure breakdown by cohort';

  chartId: string;
  subscriptions: Subscription = new Subscription();

  cohortSizeOptions: ToggleOption[] = [
    { value: '1', displayText: '1 Year' },
    { value: '2', displayText: '2 Years' },
    { value: '3', displayText: '3 Years' },
  ];

  @Input()
  set selectedCohortSize(value: string | undefined) {
    if (value) {
      this._selectedCohortSize = value;
    }
  }
  get selectedCohortSize(): string {
    return this._selectedCohortSize;
  }

  private _selectedCohortSize: string = '2';

  constructor(
    reportService: PeopleReportService,
    quickBuildService: QuickBuildService,
    streamsDrawerService: StreamsSubscribeDrawerService,
    authService: AuthService,
    statsigService: StatsigService,
    layoutService: WorkforceLayoutService
  ) {
    super(
      reportService,
      quickBuildService,
      streamsDrawerService,
      authService,
      statsigService,
      layoutService
    );
    this.chartId = UniqueIdGenerator.generate();

    // Register configuration properties for automatic emission
    this.registerConfigProperty('selectedCohortSize', () => this._selectedCohortSize);
  }

  protected initializeChart(): void {
    this.chart = Highcharts.chart(
      `highchartsContainer-${this.chartId}`,
      Highcharts.merge(spiderChartConfig, TenureCohortComparisonChartOptions)
    );
    this.chart.update({
      chart: {
        events: getChartEvents('right'),
      },
      xAxis: {
        categories: this.createTenureCohortCategories(parseInt(this._selectedCohortSize)),
      },
    });
    this.initializeContextMenu();
  }

  protected getChartData(): void {
    Log.d('TenureCohortComparisonComponent getChartData', this.quickBuilds);
    if (!this.quickBuilds) return;
    // Set this primarily for the chart subtitles to generate in base-report
    this.quickBuild = this.quickBuilds[0];

    this.showLoading();

    const sub = forkJoin(
      this.quickBuilds
        .filter((quickBuild) => quickBuild)
        .map((quickBuild) => {
          return this.addQuickbuildToChart(quickBuild);
        })
    ).subscribe({
      next: (data: (ReportResponseWithName | null)[]) => {
        this.chartLoadingComplete();
        const tenureCategories = this.createTenureCohortCategories(
          parseInt(this._selectedCohortSize)
        );
      },
      error: (error) => {
        Log.e('TenureCohortComparisonComponent getChartData error', error);
        this.showChartError(error);
      },
    });

    this.subscriptions.add(sub);
  }

  addQuickbuildToChart(quickBuild: QuickBuild): Observable<ReportResponseWithName | null> {
    Log.d('TenureCohortComparisonComponent addQuickbuildToChart', quickBuild);
    this.removeQuickbuildFromChart(quickBuild.id);

    // Find the moneyball filter group which contains the company filters
    const moneyballFilterGroup = QuickBuildService.findFilterGroupById(
      'moneyball',
      quickBuild.rootFilterGroup
    );

    if (!moneyballFilterGroup) {
      Log.e('TenureCohortComparisonComponent', 'No moneyball filter group found in quickBuild');
      return of(null);
    }

    // Find the jobs_search filter to get company information
    const jobsFilter = this.findFilterByField(moneyballFilterGroup, 'jobs_search');
    if (!jobsFilter) {
      Log.e('TenureCohortComparisonComponent', 'No jobs_search filter found in quickBuild');
      return of(null);
    }

    const jobsFilterSearch = jobsFilter.filter as SearchFilter;

    if (!jobsFilterSearch.string_values || jobsFilterSearch.string_values.length === 0) {
      Log.e('TenureCohortComparisonComponent', 'No company found in quickBuild');
      return of(null);
    }

    const companyString = jobsFilterSearch.string_values[0];

    const cohortSize = parseInt(this._selectedCohortSize);

    // Build report parameters for tenure bucketed data
    const reportParams: ReportTenureDistributionParams = {
      slice_size: cohortSize,
      number_of_buckets: 5,
    };

    return this.reportService
      .getReportDataWithReportDetails(
        this.reportId,
        REPORT_NAME,
        reportParams,
        this.reportTarget,
        quickBuild
      )
      .pipe(
        tap((response) => {
          Log.d('TenureCohortComparisonComponent addQuickbuildToChart response', response);
          // The tenure data should be in response.data (assuming it's bucketed tenure data)
          const tenureData = response.data.tenure_distribution;
          this.generateChartSeries(quickBuild.id, quickBuild.name, tenureData, response.count);
        }),
        map((data) => ({
          name: quickBuild.name,
          data: data,
        })),
        catchError((error) => {
          Log.e('TenureCohortComparisonComponent addQuickbuildToChart error', error);
          return of(null);
        })
      );
  }

  // Helper function to find a filter by field name in a filter group
  private findFilterByField(filterGroup: UIFilterGroup, fieldName: string): UIFilter | null {
    for (const filter of filterGroup.filters) {
      if ('filter' in filter) {
        const uiFilter = filter as UIFilter;
        if (QuickBuildService.isSubFiltersFilter(uiFilter.filter)) {
          continue;
        }

        if (uiFilter.filter.field === fieldName) {
          return uiFilter;
        }
      }
    }
    return null;
  }

  generateChartSeries(
    quickBuildId: string,
    quickBuildName: string,
    tenureData: any,
    totalCount: number
  ): void {
    Log.d('TenureCohortComparisonComponent generateChartSeries', tenureData);

    totalCount = tenureData.reduce((acc: number, d: any) => acc + d.count_employees, 0);

    this.addSeriesToChart({
      custom: { id: quickBuildId, isPct: true },
      id: quickBuildId,
      name: quickBuildName,
      data: tenureData.map((d: any) => (100 * d.count_employees) / totalCount),
      type: 'line',
      colorIndex: this.quickBuilds.findIndex((qb) => qb.id === quickBuildId),
    });
  }

  removeQuickbuildFromChart(quickBuildId: string) {
    if (!this.chart) return;
    this.chart.series.forEach((series) => {
      if (series.options.custom?.id === quickBuildId) {
        series.remove();
      }
    });
  }

  async resetChartData() {
    this.subscriptions.unsubscribe();
    this.subscriptions = new Subscription();
    await this.removeAllSeriesFromChart();
  }

  private createTenureCohortCategories(cohortSize: number): string[] {
    const result: string[] = [];
    const increment = cohortSize;

    for (let i = 0; i < 5; i++) {
      const start = i * increment;
      const end = (i + 1) * increment;

      if (i === 4) {
        result.push(`${start}+ years`);
      } else {
        result.push(`${start}-${end} years`);
      }
    }

    return result;
  }

  protected getContextMenuItems(point: Highcharts.Point): ChartContextMenuItem[] {
    const series = point.series;
    const category = point.category as string;
    const quickBuildId = series.options.custom?.id;
    const quickBuild = this.quickBuilds.find((qb) => qb.id === quickBuildId);
    if (!quickBuild) {
      Log.s('TenureCohortComparisonComponent getContextMenuItems', 'error', {
        error: 'No quick build found',
        seriesCustomOptions: series.options.custom ? JSON.stringify(series.options.custom) : '',
        quickBuilds: JSON.stringify(this.quickBuilds),
      });
      return [];
    }

    Log.d('TenureCohortComparisonComponent getContextMenuItems', point);

    const qbCopy = QuickBuildService.copyQuickBuild(quickBuild);
    const index = point.index;
    const min = index * parseInt(this._selectedCohortSize) * 12;
    const max = min + parseInt(this._selectedCohortSize) * 12;
    const dateFrom = dayjs().subtract(max, 'month').format('YYYY-MM');
    const dateTo = dayjs().subtract(min, 'month').format('YYYY-MM');

    const tenureFilter: UIFilter = {
      filter: {
        field: 'jobs.tenure_started_at',
        match_type: SearchFilter.MatchTypeEnum.Fuzzy,
        type: SearchFilter.TypeEnum.Must,
        date_from: index < 4 ? dateFrom : undefined,
        date_to: dateTo,
      },
    };

    QuickBuildService.addFiltersToFilterGroup(qbCopy, [tenureFilter], 'moneyball');

    return [
      {
        label: `View ${category} tenure employees at ${series.name}`,
        icon: 'group',
        action: (p) => {
          qbCopy.name = `${category} tenure employees at ${series.name}`;
          const group = QuickBuildService.findFilterGroupById('moneyball', qbCopy.rootFilterGroup);
          group!.isJobsGroup = true;
          group!.jobsGroupType = 'active';
          this.openDrawer(qbCopy);
        },
      },
    ];
  }

  toggleCohortSize(value: string) {
    if (!value) return;

    this._selectedCohortSize = value;
    this.emitConfiguration();

    // Update the chart categories
    this.chart.xAxis[0].update({
      categories: this.createTenureCohortCategories(parseInt(this._selectedCohortSize)),
    });

    this.resetChartData();
    this.getChartData();
  }
}
